import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  GaugeChart,
  getCenterPanelData,
  GaugeChartContainer,
  LabelRow,
} from "@components/Chart/highcharts/GaugeChart";
import { TypographyWithInfoIcon } from "@rentablo/components";

interface PerformanceGaugesProps {
  riskClass: number;
  performanceClass: number;
  volatility?: string;
  showVolatility?: boolean;
  maximumClass?: number;
}

export const PerformanceGauges = ({
  riskClass,
  performanceClass,
  volatility,
  showVolatility = true,
  maximumClass = 7,
}: PerformanceGaugesProps) => {
  const { t } = useTranslation();

  const classToPercentage = (classValue: number, maxClass: number) => {
    const value = Math.max(0, Math.min(classValue, maxClass));
    return (value / maxClass) * 100;
  };

  const riskPercentage = classToPercentage(riskClass, maximumClass);
  const performancePercentage = classToPercentage(
    performanceClass,
    maximumClass
  );

  const riskData = getCenterPanelData(riskClass, "risk");
  const performanceData = getCenterPanelData(performanceClass, "performance");

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="flex-start"
      gap={{ xs: 2, sm: 0 }}
    >
      <Box flex={1}>
        <TypographyWithInfoIcon
          variant="body1"
          tooltipText={t("performanceGauges.riskTooltip")}
        >
          {t("performanceGauges.risk")}
        </TypographyWithInfoIcon>
        <GaugeChartContainer>
          <GaugeChart percentage={riskPercentage} centerPanel={riskData} />
        </GaugeChartContainer>
        <LabelRow
          label={t("performanceGauges.riskClass")}
          value={riskClass || t("performanceGauges.noData")}
          sx={{ mb: showVolatility && volatility ? 2 : 0 }}
        />
        {showVolatility && volatility && (
          <LabelRow
            label={t("performanceGauges.volatility")}
            value={volatility}
          />
        )}
      </Box>
      <Box flex={1}>
        <TypographyWithInfoIcon
          variant="body1"
          tooltipText={t("performanceGauges.performanceTooltip")}
        >
          {t("performanceGauges.performance")}
        </TypographyWithInfoIcon>
        <GaugeChartContainer>
          <GaugeChart
            percentage={performancePercentage}
            centerPanel={performanceData}
          />
        </GaugeChartContainer>
        <LabelRow
          label={t("performanceGauges.performanceClass")}
          value={performanceClass || t("performanceGauges.noData")}
        />
      </Box>
    </Box>
  );
};
